<template>
  <div :class="['pos-footer', {'offline-mode': !isOnline || !hasInternet}]">
    <div class="footer-left">
      <VTooltip location="top">
        <template #activator="{ props }">
          <VBtn
            icon="mdi mdi-information-outline"
            size="x-small"
            v-bind="props"
            variant="text"
            @click="showAboutDialog = true"
          />
        </template>
        <span>About Starvly POS</span>
      </VTooltip>
      <div class="d-flex align-center ga-2">
        <VIcon
          :class="{'pulse-animation': !isOnline || !hasInternet, 'rotate-animation': connectionStatus === 'checking'}"
          :color="statusColor"
          :icon="statusIcon"
          size="16"
        />
        <span class="text-caption">{{ isOnline && hasInternet ? statusText : 'Offline' }}</span>
      </div>
      <span class="text-caption">&copy; {{ new Date().getFullYear() }}</span>
    </div>

    <div class="footer-center text-uppercase">
      {{ businessName || 'Loading...' }}
    </div>

    <div class="footer-right">
      <VTooltip location="top">
        <template #activator="{ props }">
          <div class="d-flex align-center ga-1" v-bind="props">
            <VIcon
              :class="{'rotate-animation': isSyncing}"
              :color="syncStatusColor"
              :icon="syncStatusIcon"
              size="14"
            />
            <span v-if="unsyncedCount > 0" class="text-caption">
              <VChip color="warning" label size="x-small">{{ unsyncedCount }} unsynced</VChip>
            </span>
          </div>
        </template>
        <span v-if="isSyncing">Syncing orders to server...</span>
        <span v-else-if="unsyncedCount > 0">{{ unsyncedCount }} order{{ unsyncedCount > 1 ? 's' : '' }} pending sync</span>
        <span v-else-if="lastSyncTime">Last synced: {{ formatSyncTime(lastSyncTime) }}</span>
        <span v-else>All orders synced</span>
      </VTooltip>

      <span class="text-caption">KDS:
        <VChip :color="isKdsOnline ? 'success' : 'grey'" label size="x-small">
          {{ isKdsOnline ? 'Online' : 'Offline' }}
        </VChip>
      </span>

      <span class="text-caption" style="cursor: pointer;" @click="showConnectionDialog = true">Socket:
        <VChip
          :color="socketStore.isConnected ? 'success' : (socketStore.isConnecting ? 'orange' : 'error')"
          label
          size="x-small"
        >
          {{ socketStore.isConnected ? 'Connected' : (socketStore.isConnecting ? 'Connecting...' : 'Disconnected') }}
        </VChip>
      </span>

      <VChip
        v-if="authStore.isAuthenticated"
        color="primary"
        prepend-icon="mdi mdi-account-circle-outline"
        size="small"
        variant="flat"
      >
        {{ userName }}
      </VChip>

      <VTooltip location="top">
        <template #activator="{ props }">
          <VBtn
            :icon="isFullscreen ? 'mdi mdi-fullscreen-exit' : 'mdi mdi-fullscreen'"
            size="x-small"
            v-bind="props"
            variant="text"
            @click="toggleFullscreen"
          />
        </template>
        <span>{{ isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen' }}</span>
      </VTooltip>
    </div>
  </div>

  <ConnectionStatusDialog v-model="showConnectionDialog"/>
  <AboutDialog v-model="showAboutDialog"/>
</template>

<script setup>
import {useNetworkMonitor} from '~/composables/useNetworkMonitor'
import {useOrderSync} from '~/composables/useOrderSync'
import {useKdsDetection} from '~/composables/useKdsDetection'
import {DateTime} from 'luxon'

const socketStore = useSocketStore()
const authStore = useUserStore()
const {isKdsOnline, startListening: startKdsDetection} = useKdsDetection()

const showConnectionDialog = ref(false)
const showAboutDialog = ref(false)

const {
  isOnline,
  hasInternet,
  connectionStatus,
  statusColor,
  statusIcon,
  statusText,
} = useNetworkMonitor()

const {
  isSyncing,
  lastSyncTime,
  unsyncedCount,
  updateUnsyncedCount
} = useOrderSync()

const businessName = computed(() => authStore.activeBusinessName || 'Your Business')

const userName = computed(() => {
  if (!authStore.profile) return 'User'
  const first = authStore.profile.first_name || ''
  const last = authStore.profile.last_name || ''
  if (first && last) return `${first} ${last}`
  if (first) return first
  if (last) return last
  return authStore.profile.email || 'User'
})

const syncStatusIcon = computed(() => {
  if (isSyncing.value) return 'mdi mdi-sync'
  if (unsyncedCount.value > 0) return 'mdi mdi-cloud-upload-outline'
  return 'mdi mdi-cloud-check-outline'
})

const syncStatusColor = computed(() => {
  if (isSyncing.value) return 'info'
  if (unsyncedCount.value > 0) return 'warning'
  return 'success'
})

const formatSyncTime = (isoString) => {
  return DateTime.fromISO(isoString).toRelative()
}

const isFullscreen = ref(false)

const toggleFullscreen = async () => {
  try {
    const {getCurrentWindow} = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    const currentState = await window.isFullscreen()
    await window.setFullscreen(!currentState)
    isFullscreen.value = !currentState
  } catch (error) {
    console.log('[PosFooter] Fullscreen not available')
  }
}

const checkFullscreenState = async () => {
  try {
    const {getCurrentWindow} = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    isFullscreen.value = await window.isFullscreen()
  } catch (error) {}
}

onMounted(async () => {
  await updateUnsyncedCount()
  await checkFullscreenState()
  startKdsDetection()
})

watch(() => socketStore.isConnected, async (connected) => {
  if (connected) {
    await updateUnsyncedCount()
  }
})
</script>

<style scoped>
.pos-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  background: rgba(var(--v-theme-surface), 0.95);
  border-top: 1px solid rgba(var(--v-border-color), 0.12);
  font-size: 12px;
  flex-shrink: 0;
}

.pos-footer.offline-mode {
  background: rgba(var(--v-theme-error), 0.1);
  border-top-color: rgb(var(--v-theme-error));
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.footer-center {
  font-weight: 500;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

@keyframes pulse-animation {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
}

@keyframes rotate-animation {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.pulse-animation {
  animation: pulse-animation 1.5s ease-in-out infinite;
}

.rotate-animation {
  animation: rotate-animation 2s linear infinite;
}
</style>
